/**
 * Ask the court to let this client appear remotely (Lloyd, 16 Sep 2026).
 *
 * 🔴 **A button, deliberately.** His first email read as "send these to every court"; he reversed it
 * the same morning — "I don't think I want this to be automated for every single case. I think this
 * would annoy too many courts and would backfire on us. Rather, my thought, is that this should be a
 * button, similar to and in front of the motion to reset." So this panel sits directly above the
 * continuance motion, produces one PDF, and sends nothing.
 *
 * It also captures what the court said back, because his own note on the template asks for a running
 * list of which courts allow it. That answer is saved against the COURT, so the next case at the same
 * court starts from the answer rather than asking again.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import RemoteAppearanceApi, {
  RemoteAppearanceBlockedError,
  RemoteAppearancePolicy,
  RemoteAppearanceReadinessDto,
} from '../../api/RemoteAppearanceApi';

interface Props {
  Ticket_guid: string;
  courtName?: string | null;
}

const SETTING_TYPES = ['pretrial', 'arraignment', 'docket call', 'the setting'];

const POLICY_OPTIONS: { value: RemoteAppearancePolicy | ''; label: string }[] = [
  { value: '', label: 'Not asked yet' },
  { value: 'granted', label: 'Allowed' },
  { value: 'denied', label: 'Refused' },
  { value: 'no-local-form', label: 'Allowed, but wants its own form' },
];

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

const RemoteAppearancePanel: React.FC<Props> = ({ Ticket_guid, courtName }) => {
  const [settingDate, setSettingDate] = useState('');
  const [settingType, setSettingType] = useState(SETTING_TYPES[0]);
  const [readiness, setReadiness] = useState<RemoteAppearanceReadinessDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [policySaved, setPolicySaved] = useState<string | null>(null);

  const refresh = useCallback(() => {
    RemoteAppearanceApi.readiness(Ticket_guid, { settingDate, settingType })
      .then(setReadiness)
      .catch(() => setReadiness(null));
  }, [Ticket_guid, settingDate, settingType]);

  useEffect(refresh, [refresh]);

  const generate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setBlockers([]);
    try {
      const letter = await RemoteAppearanceApi.generate(Ticket_guid, { settingDate, settingType });
      if (letter.pdfBase64) {
        window.open(pdfObjectUrl(letter.pdfBase64), '_blank');
      }
    } catch (err) {
      if (err instanceof RemoteAppearanceBlockedError) {
        setBlockers(err.blockers.map((b) => b.reason));
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the letter');
      }
    } finally {
      setBusy(false);
    }
  };

  const recordPolicy = async (policy: RemoteAppearancePolicy | ''): Promise<void> => {
    if (!readiness?.courtGuid) return;
    try {
      await RemoteAppearanceApi.recordCourtPolicy(readiness.courtGuid, policy || null);
      setPolicySaved('Saved against this court.');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record the court's answer");
    }
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <VideocamIcon color="action" />
          <Typography variant="h6" component="h2">
            Remote appearance request
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          A letter asking the court to let this client attend the setting by video. It is never sent
          automatically. Generate it, read it, and send it yourself.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Setting date"
            type="date"
            value={settingDate}
            onChange={(e) => setSettingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Setting type"
            value={settingType}
            onChange={(e) => setSettingType(e.target.value)}
            fullWidth
          >
            {SETTING_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {(readiness?.warnings ?? []).map((warning) => (
          <Alert severity="info" key={warning} sx={{ mb: 1 }}>
            {warning}
            {readiness?.courtPolicyNote ? ` ${readiness.courtPolicyNote}` : ''}
          </Alert>
        ))}

        {blockers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {blockers.map((reason) => (
              <Typography variant="body2" key={reason}>
                {reason}
              </Typography>
            ))}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={() => void generate()}
          disabled={busy || !settingDate}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <VideocamIcon />}
        >
          {busy ? 'Preparing…' : 'Generate the letter'}
        </Button>

        {readiness?.courtGuid && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              What did {courtName || 'this court'} say? Recorded against the court, so the next case
              here starts from the answer.
            </Typography>
            <TextField
              select
              size="small"
              label="This court's answer"
              value={readiness.courtPolicy ?? ''}
              onChange={(e) => void recordPolicy(e.target.value as RemoteAppearancePolicy | '')}
              sx={{ minWidth: 280 }}
            >
              {POLICY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {policySaved && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {policySaved}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RemoteAppearancePanel;
